import { useCallback, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertOctagon, Loader2, QrCode, ScanLine, Search, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusPill } from "../../components/common/StatusPill";
import { QrScanner } from "../../components/common/QrScanner";
import { GymCheckinCode } from "../../components/desk/GymCheckinCode";
import { toast } from "../../components/ui/sonner";

const DECISION_STATUS = { allow: "active", deny: "overdue", flag: "expiring" };
const REASON_COPY = {
  NO_MEMBERSHIP: "No membership on file", EXPIRED: "Membership expired", SESSIONS_EXHAUSTED: "No sessions left",
  FROZEN: "Membership is frozen", INACTIVE: "Membership inactive", ROAMING_NOT_ALLOWED: "Not their home branch",
};

// UX-FD-01 §1 SCR-P02-20/21/22: check-in, guest day-pass, and override all live on one screen —
// fewer navigations mid-queue at a busy front desk. Override (`POST /checkin/{id}/override`) is
// Manager+/Super-Admin-only in the existing backend (Prompt 03's original design,
// `checkin.override` is not on the default front_of_house_fd role) — the button below only
// renders if `me.permissions` actually includes it, server-enforced AND hidden, never just
// disabled, per this build's own "absent, not disabled" rule for out-of-tier actions.
export default function CheckIn() {
  const { me, branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [result, setResult] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [guestName, setGuestName] = useState("");
  const [panel, setPanel] = useState(null); // null | "scan" | "gymcode"
  const canOverride = me?.permissions?.includes("checkin.override");

  const searchQ = useQuery({
    queryKey: ["desk-checkin-search", submitted],
    queryFn: async () => {
      const isPhone = /^\+?\d[\d\s-]{5,}$/.test(submitted);
      const params = isPhone ? { phone: submitted.replace(/[\s-]/g, "") } : { name: submitted };
      return (await api.get("/members", { params: { ...params, limit: 10 } })).data.members;
    },
    enabled: !!submitted,
  });

  const checkinMutation = useMutation({
    mutationFn: (memberId) => api.post("/checkin", { branch_id: branchId, method: "manual", member_ref: memberId }),
    onSuccess: (res) => setResult(res.data),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const scanMemberMutation = useMutation({
    mutationFn: (payload) => api.post("/checkin/scan-member", { branch_id: branchId, payload }),
    onSuccess: (res) => {
      setPanel(null);
      setResult(res.data);
      if (res.data.decision === "allow") toast.success(`${res.data.member?.name || "Member"} checked in.`);
    },
    onError: (e) => { setPanel(null); toast.error(formatApiError(e).message); },
  });
  const { mutate: scanMember } = scanMemberMutation;
  const handleMemberScan = useCallback((decoded) => scanMember(decoded), [scanMember]);

  const guestMutation = useMutation({
    mutationFn: () => api.post("/guest/checkin", { branch_id: branchId, method: "manual", guest_ref: guestName, day_pass_valid: true }),
    onSuccess: (res) => { setResult(res.data); setGuestName(""); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const overrideMutation = useMutation({
    mutationFn: () => api.post(`/checkin/${result.id}/override`, { reason: overrideReason }),
    onSuccess: () => {
      toast.success("Entry overridden and logged.");
      setResult({ ...result, decision: "allow" });
      setOverrideReason("");
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={DESK.checkinPage} className="max-w-lg space-y-6">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Check-in</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Search by name or phone, or use manual/QR entry.</p>
      </motion.div>

      <motion.form {...fadeUp(reducedMotion, 0.04)} onSubmit={(e) => { e.preventDefault(); setResult(null); setSubmitted(query.trim()); }} className="flex gap-2">
        <Input data-testid={DESK.checkinSearchInput} placeholder="Name, phone, or manual member code…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Button type="submit" disabled={!query.trim()} className="text-white gap-2" style={{ background: "#141416" }}><Search size={15} /> Find</Button>
      </motion.form>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="flex gap-2">
        <Button
          data-testid={DESK.scanMemberButton} variant="outline" className="flex-1 gap-2"
          onClick={() => { setResult(null); setPanel(panel === "scan" ? null : "scan"); }}
        >
          <ScanLine size={15} /> {panel === "scan" ? "Close scanner" : "Scan member's QR"}
        </Button>
        <Button
          data-testid={DESK.showGymCodeButton} variant="outline" className="flex-1 gap-2"
          onClick={() => setPanel(panel === "gymcode" ? null : "gymcode")}
        >
          <QrCode size={15} /> {panel === "gymcode" ? "Hide gym code" : "Show gym's code"}
        </Button>
      </motion.div>

      {panel === "scan" && (
        <div className="rounded-[14px] p-5" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          {scanMemberMutation.isPending
            ? <p className="text-sm flex items-center gap-2" style={{ color: "#68686E" }}><Loader2 size={14} className="animate-spin" /> Checking in…</p>
            : <QrScanner onScan={handleMemberScan} onCancel={() => setPanel(null)} />}
        </div>
      )}

      {panel === "gymcode" && (
        <div className="rounded-[14px] p-5" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="text-sm font-semibold mb-3 text-center" style={{ color: "#141416" }}>Members scan or type this to check themselves in</p>
          <GymCheckinCode branchId={branchId} />
        </div>
      )}

      {searchQ.isLoading && <p className="text-sm flex items-center gap-2" style={{ color: "#68686E" }}><Loader2 size={14} className="animate-spin" /> Searching…</p>}
      {searchQ.isSuccess && searchQ.data.length === 0 && <p className="text-sm" style={{ color: "#68686E" }}>No member found.</p>}

      {searchQ.data?.map((m) => (
        <div key={m.id} className="flex items-center justify-between rounded-[12px] px-4 py-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <div>
            <p className="font-semibold text-sm" style={{ color: "#141416" }}>{m.profile.name}</p>
            <p className="text-xs" style={{ color: "#68686E" }}>{m.profile.phone}</p>
          </div>
          <Button data-testid={DESK.checkinButton(m.id)} disabled={checkinMutation.isPending} onClick={() => checkinMutation.mutate(m.id)} className="text-white gap-1.5" style={{ background: "#D6202B" }}>
            <ShieldCheck size={14} /> Check in
          </Button>
        </div>
      ))}

      {result && (
        <motion.div {...fadeUp(reducedMotion)} data-testid={DESK.checkinResult} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <div className="flex items-center gap-2">
            <StatusPill status={DECISION_STATUS[result.decision]} label={result.decision.toUpperCase()} />
            <span className="text-sm" style={{ color: "#68686E" }}>{REASON_COPY[result.reason] || result.reason || ""}</span>
          </div>
          {result.decision !== "allow" && canOverride && (
            <div className="space-y-2">
              <Input data-testid={DESK.checkinOverrideReasonInput} placeholder="Reason for override (required)" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
              <Button
                data-testid={DESK.checkinOverrideSubmit}
                disabled={!overrideReason.trim() || overrideMutation.isPending}
                onClick={() => overrideMutation.mutate()}
                variant="outline" className="gap-1.5" style={{ color: "var(--scarlet-neg)", borderColor: "var(--scarlet-neg)" }}
              >
                <AlertOctagon size={13} /> Override & let in
              </Button>
            </div>
          )}
        </motion.div>
      )}

      <motion.div {...fadeUp(reducedMotion, 0.1)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold" style={{ color: "#141416" }}>Guest / day pass</p>
        <div className="flex gap-2">
          <Input data-testid={DESK.guestNameInput} placeholder="Guest name" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
          <Button data-testid={DESK.guestCheckinButton} disabled={!guestName.trim() || guestMutation.isPending} onClick={() => guestMutation.mutate()} className="text-white" style={{ background: "#141416" }}>
            Check in guest
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
